import {
  CheckRounded,
  ChevronRightSharp,
  ClearRounded,
  EditOutlined,
  SaveOutlined,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  Box,
  keyframes,
  Link,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import supersub from 'remark-supersub';

import { AugmentedResponseWithSources } from '../../../../components/DealGPT/SourcesDisplay';
import useQuestionStatusInfo from '../../../../hooks/useQuestionStatusInfo';
import { selectFolders } from '../../../../redux/reducers/folderReducer';
import { updateProjectKpiStatus, userEditSave } from '../../../../services/dealGPT';
import { StyledAccordionSummary, StyledIconButton } from '../../../../theme/CustomComponents';
import { ProjectRunAnalysisQuestion } from '../../../../Types/dealGPT';
import { AnalysisQuestionStatus, UpdatedReason } from '../../../../Types/enums';
import { renderMessage } from '../../../../util/messageUtils';
import { AnalyticsEvent, useAnalytics } from '../../../Providers/AnalyticsProvider';
import QuestionStatusIndicator from '../../QuestionStatusIndicator';

const pulse = keyframes`
  0% {
    border-color: rgba(249, 0, 211, 1);
  }
  50% {
    border-color: rgba(249, 0, 211, 0.3);
  }
  100% {
    border-color: rgba(249, 0, 211, 1);
  }
`;

interface KpiQuestionAccordionProps {
  question: ProjectRunAnalysisQuestion;
  isProcessing: boolean;
  autoScroll?: boolean;
  closeDrawer: () => void;
  refetchProjectConfig: () => Promise<void>;
}

const KpiQuestionAccordion: React.FC<KpiQuestionAccordionProps> = ({
  question,
  isProcessing,
  autoScroll,
  closeDrawer,
  refetchProjectConfig,
}) => {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const [editingValue, setEditingValue] = useState<string | null>(null);
  const ref = useRef<null | HTMLDivElement>(null);

  const { currentFolder } = useSelector(selectFolders);

  const projectId = currentFolder?.projectId || '';
  const { statusColor, statusExplanation } = useQuestionStatusInfo(question.status, question.value);

  const handleAccordionChange = useCallback(
    (expanded: boolean) => {
      analytics.event(
        AnalyticsEvent.WM_IA_PROJECT_OVERVIEW_KPI_EXPAND,
        {
          id: projectId,
          value: `isExpanded=${expanded}`,
        },
        {
          projectId,
          isExpanded: expanded,
        }
      );
    },
    [projectId, analytics]
  );

  const handleSaveValue = useCallback(async () => {
    try {
      if (!editingValue) return;

      // If the user is saving a manual response, we auto confirm it
      await userEditSave({
        ...question,
        value: editingValue,
        status: AnalysisQuestionStatus.CONFIRMED,
        updatedReason: UpdatedReason.USER_EDIT,
      });

      refetchProjectConfig();
      setEditingValue(null);
      enqueueSnackbar('Basic Fact updated and confirmed successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to save content. Please try again later.', {
        variant: 'error',
      });
      return;
    }
  }, [question, editingValue, refetchProjectConfig]);

  const handleConfirm = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (!projectId) return;
      e.stopPropagation();
      analytics.event(
        AnalyticsEvent.WM_IA_A_KPI_CONFIRM,
        {
          id: question.id,
          value: `Project=${projectId},Run=${question.runId},Scope=${question.scope}`,
        },
        {
          projectId,
          runId: question.runId,
          scope: question.scope,
          questionId: question.id,
        }
      );

      await updateProjectKpiStatus(projectId, question, AnalysisQuestionStatus.CONFIRMED);
      refetchProjectConfig();
      enqueueSnackbar('Response confirmed successfully', { variant: 'success' });
    },
    [projectId, question, refetchProjectConfig, analytics]
  );

  const onClickViewMore = useCallback(() => {
    analytics.event(
      AnalyticsEvent.WM_IA_PROJECT_OVERVIEW_KPI_VIEW_MORE,
      {
        id: question.id,
      },
      {
        questionId: question.id,
      }
    );
    navigate(`/folder/${currentFolder?.id}/configure/kpi?questionId=${question.id}`);
    closeDrawer();
  }, [currentFolder, question, analytics, navigate, closeDrawer]);

  // If the kpi is newly created, scroll to it
  useEffect(() => {
    if (autoScroll && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [autoScroll]);

  return (
    <Accordion
      key={question.id}
      onChange={(_, newValue) => handleAccordionChange(newValue)}
      ref={ref}
      sx={{
        margin: '0px !important',
        ...(isProcessing && {
          // Pulse border color every second while processing
          border: '2px solid #F900D3',
          borderRadius: 2,
          animation: `${pulse} 1s ease-in-out`,
          animationIterationCount: 'infinite',
        }),
      }}
    >
      <StyledAccordionSummary expandIcon={<ChevronRightSharp />}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            gap: 1,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              minWidth: '40%',
              maxWidth: '40%',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {question.prettyQuestion}?
          </Typography>
          {editingValue !== null ? (
            <TextField
              label="Response"
              variant="outlined"
              size="small"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveValue();
                }
              }}
              fullWidth
              autoFocus
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                gap: 1,
              }}
            >
              {/* Status indicator */}
              {!isProcessing && (
                <QuestionStatusIndicator explanation={statusExplanation} color={statusColor} />
              )}
              <Typography
                sx={{
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                }}
                variant="body1"
              >
                {isProcessing ? 'Processing...' : question.value || 'Missing'}
              </Typography>
            </Box>
          )}

          {editingValue !== null ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Save" arrow>
                <StyledIconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveValue();
                  }}
                  disabled={!editingValue}
                >
                  <SaveOutlined />
                </StyledIconButton>
              </Tooltip>
              <Tooltip title="Cancel" arrow>
                <StyledIconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingValue(null);
                  }}
                >
                  <ClearRounded />
                </StyledIconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {question.status !== AnalysisQuestionStatus.CONFIRMED && question.value && (
                <Tooltip title="Confirm" arrow>
                  <StyledIconButton disabled={isProcessing} onClick={handleConfirm}>
                    <CheckRounded />
                  </StyledIconButton>
                </Tooltip>
              )}
              <Tooltip title="Edit response" arrow>
                <StyledIconButton
                  disabled={isProcessing}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingValue(question.value || '');
                  }}
                >
                  <EditOutlined />
                </StyledIconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </StyledAccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body1">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, supersub]}
              components={{
                sup: ({ children }) => (
                  <AugmentedResponseWithSources
                    text={children.toString()}
                    sources={question.documents ?? []}
                  />
                ),
              }}
            >
              {renderMessage({
                message: question.response || '',
                sources: question.documents || [],
              })}
            </ReactMarkdown>
          </Typography>
          <Link sx={{ cursor: 'pointer' }} onClick={onClickViewMore}>
            Click here to view more
          </Link>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default KpiQuestionAccordion;
